(() => {
  const controllers = new WeakMap();
  const hasReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const EASING_SMOOTH_IN = [0.4, 0, 1, 1];
  const EASING_ELEGANT = [0.4, 0, 0.2, 1];

  const getFocusableElements = (container) => {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');
  };

  class HeaderNavController {
    constructor(root) {
      this.root = root;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.sectionContainer = root.closest(`#shopify-section-${root.dataset.sectionId}`) || root.closest('[id^="shopify-section-"]');

      this.desktopNav = root.querySelector('.header__desktop-nav');
      this.desktopOverlay = root.querySelector('[data-header-desktop-overlay]');
      this.menuItems = Array.from(root.querySelectorAll('[data-header-menu-item]'));
      this.mobileToggleButton = root.querySelector('[data-header-mobile-toggle]');
      this.mobileOpenButton = this.mobileToggleButton || root.querySelector('[data-header-mobile-open]');
      this.mobileCloseButtons = Array.from(root.querySelectorAll('[data-header-mobile-close]'));
      this.mobileOverlay = root.querySelector('[data-header-mobile-overlay]');
      this.mobileDrawer = root.querySelector('[data-header-mobile-drawer]');
      this.mobileBody = root.querySelector('.header__mobile-body');
      this.mobileRootView = root.querySelector('[data-mobile-root]');
      this.mobileBranchView = root.querySelector('[data-mobile-branch]');
      this.mobileBranchBack = root.querySelector('[data-mobile-branch-back]');
      this.mobileBranchTitle = root.querySelector('[data-mobile-branch-title]');
      this.mobileBranchList = root.querySelector('[data-mobile-branch-list]');
      this.mobileStepButtons = Array.from(root.querySelectorAll('[data-mobile-step-button]'));
      this.mobileTabs = Array.from(root.querySelectorAll('[data-mobile-tab]'));
      this.mobileTabPanels = Array.from(root.querySelectorAll('[data-mobile-tab-panel]'));
      this.mobileDrillButtons = Array.from(root.querySelectorAll('[data-mobile-nav-drill]'));

      this.searchOpenButton = root.querySelector('[data-header-search-open]');
      this.searchInline = root.querySelector('[data-header-search-inline]');
      this.searchOverlay = root.querySelector('[data-header-search-overlay]');
      this.searchPanel = root.querySelector('[data-header-search-panel]');
      this.searchCloseButtons = Array.from(root.querySelectorAll('[data-header-search-close]'));
      this.desktopSearchForm = root.querySelector('[data-header-search-form-desktop]');
      this.desktopSearchInput = root.querySelector('[data-header-search-input-desktop]');
      this.desktopSearchGuided = root.querySelector('[data-header-search-guided-desktop]');
      this.desktopSearchPredictive = root.querySelector('[data-header-predictive-desktop]');
      this.desktopSearchResultsTitle = root.querySelector('[data-header-search-results-title]');
      this.mobileSearchForm = root.querySelector('[data-header-search-form-mobile]');
      this.mobileSearchInput = root.querySelector('[data-header-search-input-mobile]');
      this.mobileSearchGuided = root.querySelector('[data-header-search-guided-mobile]');
      this.mobileSearchPredictive = root.querySelector('[data-header-predictive-mobile]');
      this.mobileSearchResultsTitle = root.querySelector('[data-header-mobile-search-results-title]');
      this.searchChipButtons = Array.from(root.querySelectorAll('[data-search-chip-query]'));

      this.desktopOpenItem = null;
      this.lastFocusedTrigger = null;
      this.activeFocusTrap = null;
      this.focusTrapHandler = null;
      this.mobileCloseInProgress = false;
      this.searchCloseInProgress = false;
      this.desktopSearchOpen = false;
      this.mobileViewTransitionInProgress = false;
      this.searchAnimationToken = 0;
      this.searchState = {
        desktop: {
          debounceTimer: null,
          requestController: null
        },
        mobile: {
          debounceTimer: null,
          requestController: null
        }
      };

      this.mobileMenus = this.readMobileMenus();
      this.mobileState = {
        branch: null,
        stack: []
      };

      this._gorgiasStyleBackup = null;
      this._gorgiasDomMo = null;
      this._gorgiasAttrMo = null;

      this.init();
    }

    init() {
      this.bindStickyOffsetSync();
      this.bindDesktopDropdowns();
      this.bindRichPanelTabs();
      this.bindMobileDrawer();
      this.bindSearchExperience();
      this.bindGlobalShortcuts();
    }

    destroy() {
      this.abortController.abort();
      this.clearAllSearchState();
      this.releaseFocusTrap();
      this.teardownGorgiasChatMobileNavSuppression();

      if (this.sectionContainer) {
        this.sectionContainer.style.removeProperty('--header-sticky-top');
        this.sectionContainer.style.removeProperty('--header-layer-z');
      }
    }

    /**
     * Gorgias injects #gorgias-chat-container async and may keep it visible above the drawer.
     * Collapse it with display:none while the mobile menu is open; restore prior inline display on close.
     */
    getGorgiasChatContainer() {
      return document.getElementById('gorgias-chat-container');
    }

    backupGorgiasChatInlineStyles(el) {
      if (this._gorgiasStyleBackup) return;
      this._gorgiasStyleBackup = {
        display: el.style.getPropertyValue('display'),
        displayPriority: el.style.getPropertyPriority('display'),
      };
    }

    reassertGorgiasChatHiddenForMobileNav() {
      const el = this.getGorgiasChatContainer();
      if (!el) return;

      this.backupGorgiasChatInlineStyles(el);
      this._gorgiasAttrMo?.disconnect();

      el.style.setProperty('display', 'none', 'important');

      if (!this._gorgiasAttrMo) {
        this._gorgiasAttrMo = new MutationObserver(() => {
          if (this.mobileDrawer?.dataset.open !== 'true') return;
          this.reassertGorgiasChatHiddenForMobileNav();
        });
      }
      this._gorgiasAttrMo.observe(el, { attributes: true, attributeFilter: ['style'] });
    }

    startGorgiasChatMobileNavSuppression() {
      this.reassertGorgiasChatHiddenForMobileNav();

      const retryDelaysMs = [50, 200, 600, 2000];
      retryDelaysMs.forEach((ms) => {
        window.setTimeout(() => {
          if (this.mobileDrawer?.dataset.open === 'true') {
            this.reassertGorgiasChatHiddenForMobileNav();
          }
        }, ms);
      });

      if (this._gorgiasDomMo) return;
      this._gorgiasDomMo = new MutationObserver(() => {
        if (this.mobileDrawer?.dataset.open === 'true') {
          this.reassertGorgiasChatHiddenForMobileNav();
        }
      });
      this._gorgiasDomMo.observe(document.documentElement, { childList: true, subtree: true });
    }

    restoreGorgiasChatAfterMobileNav() {
      const el = this.getGorgiasChatContainer();
      const backup = this._gorgiasStyleBackup;
      this._gorgiasStyleBackup = null;

      if (!el || !backup) return;

      if (backup.display) el.style.setProperty('display', backup.display, backup.displayPriority || '');
      else el.style.removeProperty('display');
    }

    teardownGorgiasChatMobileNavSuppression() {
      this._gorgiasDomMo?.disconnect();
      this._gorgiasDomMo = null;
      this._gorgiasAttrMo?.disconnect();
      this._gorgiasAttrMo = null;
      this.restoreGorgiasChatAfterMobileNav();
    }

    readMobileMenus() {
      const result = {};

      const scripts = this.mobileDrawer?.querySelectorAll('[data-mobile-menu-json]') || [];
      scripts.forEach((script) => {
        const key = script.dataset.mobileMenuJson;
        if (!key) return;

        try {
          const parsed = JSON.parse(script.textContent || '[]');
          result[key] = Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          result[key] = [];
        }
      });

      return result;
    }

    canAnimate() {
      return !hasReducedMotion() && !!window.Motion && typeof window.Motion.animate === 'function';
    }

    wait(ms = 0) {
      if (!ms || ms <= 0) return Promise.resolve();
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    }

    waitForTransition(target, propertyName, timeoutMs = 420) {
      if (!target || hasReducedMotion()) return Promise.resolve();

      return new Promise((resolve) => {
        let settled = false;

        const finish = () => {
          if (settled) return;
          settled = true;
          target.removeEventListener('transitionend', onTransitionEnd);
          window.clearTimeout(timerId);
          resolve();
        };

        const onTransitionEnd = (event) => {
          if (event.target !== target) return;
          if (propertyName && event.propertyName !== propertyName) return;
          finish();
        };

        const timerId = window.setTimeout(finish, timeoutMs);
        target.addEventListener('transitionend', onTransitionEnd);
      });
    }

    bindStickyOffsetSync() {
      const sync = () => this.syncStickyOffset();
      sync();

      window.addEventListener('scroll', sync, { signal: this.signal, passive: true });
      window.addEventListener('resize', sync, { signal: this.signal });
      window.addEventListener('orientationchange', sync, { signal: this.signal });

      const desktopQuery = window.matchMedia('(min-width: 1024px)');
      desktopQuery.addEventListener('change', (e) => {
        if (e.matches) {
          this.closeMobileDrawer({ restoreFocus: false });
        } else {
          this.closeDesktopSearch({ restoreFocus: false, clearQuery: true });
          this.closeDesktopItems();
        }
      }, { signal: this.signal });

      document.addEventListener('shopify:section:load', (event) => {
        if (event.target.matches?.('.section-announcement-bar')) {
          sync();
        }
      }, { signal: this.signal });

      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.matches?.('.section-announcement-bar')) {
          sync();
        }
      }, { signal: this.signal });
    }

    syncStickyOffset() {
      if (!this.sectionContainer) return;

      const announcement = document.querySelector('.announcement-bar');
      let offset = 0;

      if (announcement) {
        const styles = window.getComputedStyle(announcement);
        if (styles.display !== 'none' && styles.visibility !== 'hidden') {
          const rect = announcement.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
          const visibleTop = Math.max(0, rect.top);
          const visibleBottom = Math.min(viewportHeight, rect.bottom);
          offset = Math.max(0, visibleBottom - visibleTop);
        }
      }

      this.sectionContainer.style.setProperty('--header-sticky-top', `${Math.round(Math.max(0, offset))}px`);
    }

    syncSectionLayer() {
      if (!this.sectionContainer) return;

      const mobileOpen = this.mobileDrawer && !this.mobileDrawer.hidden;
      const searchOpen = this.desktopSearchOpen;
      if (mobileOpen || searchOpen) {
        this.sectionContainer.style.setProperty('--header-layer-z', '500');
      } else {
        this.sectionContainer.style.removeProperty('--header-layer-z');
      }
    }

    resetInlineStyles(element) {
      if (!element) return;
      element.style.opacity = '';
      element.style.transform = '';
      element.style.scale = '';
      element.style.filter = '';
    }

    bindDesktopDropdowns() {
      if (!this.desktopNav || !this.menuItems.length) return;

      const isHover = () => window.matchMedia('(hover: hover)').matches;

      this.menuItems.forEach((item) => {
        const link = item.querySelector('[data-header-menu-link]');
        const panel = item.querySelector('[data-header-panel]');
        if (!link || !panel) return;

        // Pointer enters — CSS :hover shows the panel; JS enhances with ARIA + stagger
        item.addEventListener('pointerenter', () => {
          if (!isHover()) return;
          this.closeDesktopSearch({ restoreFocus: false, clearQuery: false });
          this.setDesktopARIA(item, true);
          this.staggerPanelChildren(panel);
        }, { signal: this.signal });

        // Pointer leaves — CSS :hover hides the panel; JS cleans up ARIA
        item.addEventListener('pointerleave', () => {
          if (!isHover()) return;
          this.setDesktopARIA(item, false);
        }, { signal: this.signal });

        // Keyboard: focus into item opens it via data-open
        item.addEventListener('focusin', () => {
          this.openDesktopItem(item);
        }, { signal: this.signal });
      });

      // Keyboard: focus leaves the nav entirely — close
      this.desktopNav.addEventListener('focusout', (event) => {
        const nextTarget = event.relatedTarget;
        if (!nextTarget || !this.desktopNav.contains(nextTarget)) {
          this.closeDesktopItems();
        }
      }, { signal: this.signal });

      // Click outside header — close
      document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target)) {
          this.closeDesktopItems();
        }
      }, { signal: this.signal });

      if (this.desktopOverlay) {
        this.desktopOverlay.addEventListener('click', () => this.closeDesktopItems(), { signal: this.signal });
      }
    }

    bindRichPanelTabs() {
      const richPanels = Array.from(this.root.querySelectorAll('.header__panel--rich[data-header-panel]'));
      if (!richPanels.length) return;

      richPanels.forEach((panel) => {
        const tabButtons = Array.from(panel.querySelectorAll('[data-rich-tab-button]'));
        const tabViews = Array.from(panel.querySelectorAll('[data-rich-tab-view]'));
        if (!tabButtons.length || !tabViews.length) return;

        const activateTab = (nextButton, moveFocus = false) => {
          const targetId = nextButton?.dataset.richTarget;
          if (!targetId) return;

          if (nextButton.classList.contains('is-active') && !moveFocus) return;
          if (nextButton.classList.contains('is-active') && moveFocus) {
            nextButton.focus();
            return;
          }

          tabButtons.forEach((button) => {
            const isActive = button === nextButton;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-selected', isActive ? 'true' : 'false');
            button.tabIndex = isActive ? 0 : -1;
          });

          tabViews.forEach((view) => {
            const isActive = view.id === targetId;
            view.hidden = !isActive;
            view.setAttribute('aria-hidden', isActive ? 'false' : 'true');

            if (isActive && this.canAnimate()) {
              const cards = Array.from(view.querySelectorAll('.header__rich-card-item, .header__rich-empty'));
              if (cards.length) {
                const sequence = [];
                cards.forEach((card, i) => {
                  const delay = Math.min(0.12, i * 0.02);
                  sequence.push([card, { opacity: [0, 1], y: [8, 0] }, { duration: 0.28, easing: EASING_ELEGANT, at: delay }]);
                });
                window.Motion.animate(sequence);
              }
            }
          });

          if (moveFocus) {
            nextButton.focus();
          }
        };

        tabButtons.forEach((button, index) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            activateTab(button, true);
          }, { signal: this.signal });

          button.addEventListener('pointerenter', () => {
            if (window.matchMedia('(hover: hover)').matches) {
              activateTab(button);
            }
          }, { signal: this.signal });

          button.addEventListener('focus', () => {
            activateTab(button);
          }, { signal: this.signal });

          button.addEventListener('keydown', (event) => {
            if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
              return;
            }

            event.preventDefault();

            let nextIndex = index;
            if (event.key === 'Home') {
              nextIndex = 0;
            } else if (event.key === 'End') {
              nextIndex = tabButtons.length - 1;
            } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
              nextIndex = (index + 1) % tabButtons.length;
            } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
              nextIndex = (index - 1 + tabButtons.length) % tabButtons.length;
            }

            activateTab(tabButtons[nextIndex], true);
          }, { signal: this.signal });
        });

        activateTab(tabButtons[0], false);
      });
    }

    /* Set ARIA attributes for a desktop dropdown (used by hover path) */
    setDesktopARIA(item, open) {
      const link = item.querySelector('[data-header-menu-link]');
      const panel = item.querySelector('[data-header-panel]');
      if (!link || !panel) return;

      link.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (this.desktopOverlay) {
        this.desktopOverlay.setAttribute('aria-hidden', open ? 'false' : 'true');
      }
    }

    /* Stagger-animate child elements inside a panel (cards, tabs, etc.) */
    staggerPanelChildren(panel) {
      if (!this.canAnimate()) return;
      const stagedItems = Array.from(
        panel.querySelectorAll('.header__rich-card-item, .header__rich-simple-item, .header__panel-column')
      );
      if (!stagedItems.length) return;
      const sequence = [];
      stagedItems.forEach((item, i) => {
        const delay = Math.min(0.16, i * 0.02);
        sequence.push([item, { opacity: [0, 1], y: [10, 0] }, { duration: 0.3, easing: EASING_ELEGANT, at: delay }]);
      });
      window.Motion.animate(sequence);
    }

    /* Open a desktop dropdown for keyboard/programmatic use (sets data-open) */
    openDesktopItem(item) {
      if (this.desktopOpenItem === item) return;
      this.closeDesktopSearch({ restoreFocus: false, clearQuery: false });

      // Close the previous item
      if (this.desktopOpenItem) {
        this.setDesktopARIA(this.desktopOpenItem, false);
        delete this.desktopOpenItem.dataset.open;
        this.desktopOpenItem = null;
      }

      const link = item.querySelector('[data-header-menu-link]');
      const panel = item.querySelector('[data-header-panel]');
      if (!link || !panel) return;

      item.dataset.open = 'true';
      this.root.dataset.desktopOpen = 'true';
      this.setDesktopARIA(item, true);
      this.desktopOpenItem = item;
      this.staggerPanelChildren(panel);
    }

    closeDesktopItems() {
      if (!this.desktopOpenItem) return;

      const item = this.desktopOpenItem;
      this.desktopOpenItem = null;

      this.setDesktopARIA(item, false);
      delete item.dataset.open;
      delete this.root.dataset.desktopOpen;
    }

    animatePanelIn(panel) {
      this.staggerPanelChildren(panel);
    }

    bindMobileDrawer() {
      if (!this.mobileDrawer || !this.mobileOpenButton) return;

      if (this.mobileToggleButton) {
        this.mobileToggleButton.addEventListener('click', () => {
          const isOpen = this.mobileDrawer && !this.mobileDrawer.hidden;
          if (isOpen) {
            this.closeMobileDrawer();
          } else {
            this.openMobileDrawer(this.mobileToggleButton);
          }
        }, { signal: this.signal });
      } else {
        this.mobileOpenButton.addEventListener('click', () => {
          this.openMobileDrawer(this.mobileOpenButton);
        }, { signal: this.signal });
      }

      this.mobileCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeMobileDrawer(), { signal: this.signal });
      });

      if (this.mobileOverlay) {
        this.mobileOverlay.addEventListener('click', () => this.closeMobileDrawer(), { signal: this.signal });
      }

      this.mobileStepButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const branch = button.dataset.mobileStepSource;
          const label = button.dataset.mobileLabel || 'Menu';
          if (!branch) return;
          this.openMobileBranch(branch, label);
        }, { signal: this.signal });
      });

      this.mobileTabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const panelIndex = tab.dataset.mobileTab;
          this.switchMobileTab(panelIndex);
        }, { signal: this.signal });
      });

      this.mobileDrillButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const handle = button.dataset.mobileNavDrill;
          const label = button.dataset.mobileDrillLabel || 'Menu';
          this.openMobileDrillIn(handle, label);
        }, { signal: this.signal });
      });

      if (this.mobileBranchBack) {
        this.mobileBranchBack.addEventListener('click', () => this.mobileGoBack(), { signal: this.signal });
      }

      this.mobileBranchList?.addEventListener('click', (event) => {
        const nextButton = event.target.closest('[data-mobile-next]');
        if (nextButton) {
          event.preventDefault();
          const encoded = nextButton.dataset.mobileNext;
          if (!encoded) return;

          try {
            const nextNode = JSON.parse(encoded);
            this.mobileState.stack.push(nextNode);
            this.renderMobileBranch();
          } catch (error) {
            return;
          }

          return;
        }

        const link = event.target.closest('[data-mobile-link]');
        if (link) {
          this.closeMobileDrawer();
        }
      }, { signal: this.signal });
    }

    async openMobileDrawer(triggerEl) {
      if (!this.mobileDrawer || !this.mobileDrawer.hidden) return;
      await this.closeDesktopSearch({ restoreFocus: false, clearQuery: false });
      this.lastFocusedTrigger = triggerEl || null;

      this.mobileDrawer.hidden = false;
      this.mobileDrawer.dataset.open = 'true';
      this.mobileDrawer.setAttribute('aria-hidden', 'false');
      this.syncSectionLayer();

      if (this.mobileOverlay) {
        this.mobileOverlay.hidden = false;
      }

      this.mobileOpenButton?.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      if (this.mobileBody) this.mobileBody.scrollTop = 0;

      this.startGorgiasChatMobileNavSuppression();

      await this.showMobileRoot();
      this.activateFocusTrap(this.mobileDrawer);

        if (this.canAnimate()) {
          const sequence = [
            [this.mobileDrawer, { opacity: [0, 1], y: [6, 0] }, { duration: 0.24, easing: EASING_ELEGANT }],
          ];

          if (this.mobileOverlay) {
            this.mobileOverlay.style.opacity = '0';
            sequence.unshift(
              [this.mobileOverlay, { opacity: [0, 1] }, { duration: 0.2, easing: EASING_ELEGANT }]
            );
            sequence[1].at = '<';
          }

          const rootItems = Array.from(
            this.mobileDrawer.querySelectorAll('.header__mobile-tabs, .header__mobile-tab-panel.is-active, .header__mobile-nav-links, .header__mobile-shop-all-btn')
          );

          rootItems.forEach((item, i) => {
            const delay = Math.min(0.1, i * 0.02);
            sequence.push(
              [item, { opacity: [0, 1], y: [8, 0] }, { duration: 0.22, easing: EASING_ELEGANT, at: delay + 0.03 }]
            );
          });

          window.Motion.animate(sequence);
      }

      const firstFocusable = getFocusableElements(this.mobileDrawer)[0];
      firstFocusable?.focus();
    }

    async closeMobileDrawer({ restoreFocus = true } = {}) {
      if (!this.mobileDrawer || this.mobileDrawer.hidden) return;
      if (this.mobileCloseInProgress) return;
      this.mobileCloseInProgress = true;

      try {
        if (this.canAnimate()) {
          const sequence = [
            [this.mobileDrawer, { opacity: [1, 0], y: [0, 4] }, { duration: 0.18, easing: EASING_SMOOTH_IN }],
          ];
          if (this.mobileOverlay) {
            sequence.push(
              [this.mobileOverlay, { opacity: [1, 0] }, { duration: 0.2, easing: EASING_SMOOTH_IN, at: '<' }]
            );
          }

          const controls = window.Motion.animate(sequence);
          if (controls?.finished) {
            try { await controls.finished; } catch (e) { /* cancelled */ }
          } else {
            await this.wait(200);
          }
        }

        delete this.mobileDrawer.dataset.open;
        this.mobileDrawer.classList.remove('is-searching');
        const activeElement = document.activeElement;
        if (activeElement && this.mobileDrawer.contains(activeElement) && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        if (this.mobileSearchInput) {
          this.mobileSearchInput.value = '';
          const mobileForm = this.mobileSearchInput.closest('.header__mobile-search-form');
          if (mobileForm) mobileForm.classList.remove('is-focused');
        }
        this.hideGuidedSearch('mobile');
        this.hidePredictiveSearch('mobile');
        this.mobileDrawer.setAttribute('aria-hidden', 'true');
        this.mobileDrawer.hidden = true;
        this.resetInlineStyles(this.mobileDrawer);

        if (this.mobileOverlay) {
          this.mobileOverlay.hidden = true;
          this.resetInlineStyles(this.mobileOverlay);
        }

        this.mobileOpenButton?.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        this.syncSectionLayer();

        this.teardownGorgiasChatMobileNavSuppression();

        await this.showMobileRoot();
        this.releaseFocusTrap();
        if (restoreFocus) this.restoreTriggerFocus();
      } finally {
        this.mobileCloseInProgress = false;
      }
    }

    async openMobileBranch(branch, label) {
      if (this.mobileViewTransitionInProgress) return;

      const nodes = this.mobileMenus[branch] || [];
      this.mobileState.branch = branch;
      this.mobileState.stack = [{
        title: label,
        links: nodes
      }];
      if (this.mobileBody) this.mobileBody.scrollTop = 0;

      this.renderMobileBranch();
      if (!this.mobileRootView || !this.mobileBranchView) return;

      const rootWasVisible = !this.mobileRootView.hidden;
      this.mobileBranchView.hidden = false;

      if (rootWasVisible && this.canAnimate()) {
        await this.transitionMobileViews(this.mobileRootView, this.mobileBranchView, 'forward');
      } else {
        if (this.mobileRootView) this.mobileRootView.hidden = true;
        this.mobileBranchView.hidden = false;
      }

      const firstFocusable = getFocusableElements(this.mobileBranchView)[0];
      firstFocusable?.focus();
    }

    async showMobileRoot({ animate = false } = {}) {
      this.mobileState.branch = null;
      this.mobileState.stack = [];
      if (this.mobileBody) this.mobileBody.scrollTop = 0;

      if (!this.mobileRootView || !this.mobileBranchView) return;

      const branchWasVisible = !this.mobileBranchView.hidden;

      if (animate && branchWasVisible && this.canAnimate()) {
        await this.transitionMobileViews(this.mobileBranchView, this.mobileRootView, 'back');
      } else {
        this.mobileRootView.hidden = false;
        this.mobileBranchView.hidden = true;
      }
    }

    async transitionMobileViews(exitView, enterView, direction = 'forward') {
      if (this.mobileViewTransitionInProgress) return;
      this.mobileViewTransitionInProgress = true;

      const isForward = direction === 'forward';
      const exitX = isForward ? '-20px' : '20px';
      const enterX = isForward ? '24px' : '-24px';

      // Both visible and absolutely positioned so they overlap
      enterView.hidden = false;
      exitView.classList.add('is-transitioning');
      enterView.classList.add('is-transitioning');

      try {
        const sequence = [
          [exitView, { opacity: [1, 0], x: [0, exitX] }, { duration: 0.22, easing: EASING_SMOOTH_IN }],
          [enterView, { opacity: [0, 1], x: [enterX, 0] }, { duration: 0.34, easing: EASING_ELEGANT, at: '-0.08' }],
        ];

        const controls = window.Motion.animate(sequence);
        if (controls?.finished) {
          await controls.finished;
        } else {
          await this.wait(480);
        }
      } catch (error) {
        // Animation cancelled — clean up regardless
      }

      // Clean up: hide exiting, remove positioning, clear inline styles
      exitView.hidden = true;
      exitView.classList.remove('is-transitioning');
      enterView.classList.remove('is-transitioning');
      this.resetInlineStyles(exitView);
      this.resetInlineStyles(enterView);
      this.mobileViewTransitionInProgress = false;
    }

    async mobileGoBack() {
      if (this.mobileState.stack.length > 1) {
        this.mobileState.stack.pop();
        this.renderMobileBranch();
        return;
      }

      await this.showMobileRoot({ animate: true });
    }

    switchMobileTab(panelIndex) {
      const tabsContainer = this.mobileTabs[0]?.closest('.header__mobile-tabs');
      if (tabsContainer) tabsContainer.dataset.activeTab = panelIndex;

      this.mobileTabs.forEach((tab) => {
        const isActive = tab.dataset.mobileTab === panelIndex;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.mobileTabPanels.forEach((panel) => {
        const isActive = panel.dataset.mobileTabPanel === panelIndex;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;

        if (isActive && this.canAnimate()) {
          const sequence = [
            [panel, { opacity: [0, 1], x: [12, 0] }, { duration: 0.32, easing: EASING_ELEGANT }],
          ];
          const cards = Array.from(panel.querySelectorAll('.header__mobile-card'));
          cards.forEach((card, i) => {
            const delay = Math.min(0.12, i * 0.02);
            sequence.push([card, { opacity: [0, 1], y: [8, 0] }, { duration: 0.3, easing: EASING_ELEGANT, at: delay + 0.06 }]);
          });
          window.Motion.animate(sequence);
        }
      });
    }

    async openMobileDrillIn(handle, label) {
      if (this.mobileViewTransitionInProgress) return;

      const template = this.root.querySelector(`[data-mobile-drill-template="${handle}"]`);
      if (!template || !this.mobileBranchList || !this.mobileBranchTitle) return;

      this.mobileBranchTitle.textContent = label;
      this.mobileBranchList.innerHTML = template.innerHTML;

      if (this.mobileBody) this.mobileBody.scrollTop = 0;
      if (!this.mobileRootView || !this.mobileBranchView) return;

      const rootWasVisible = !this.mobileRootView.hidden;
      this.mobileState.branch = handle;
      this.mobileState.stack = [{ title: label, links: [] }];

      if (rootWasVisible && this.canAnimate()) {
        await this.transitionMobileViews(this.mobileRootView, this.mobileBranchView, 'forward');
      } else {
        if (this.mobileRootView) this.mobileRootView.hidden = true;
        this.mobileBranchView.hidden = false;
      }

      const firstFocusable = getFocusableElements(this.mobileBranchView)[0];
      firstFocusable?.focus();
    }

    renderMobileBranch() {
      if (!this.mobileBranchList || !this.mobileBranchTitle) return;
      if (!this.mobileState.stack.length) return;

      const currentNode = this.mobileState.stack[this.mobileState.stack.length - 1];
      const links = Array.isArray(currentNode.links) ? currentNode.links : [];

      this.mobileBranchTitle.textContent = currentNode.title || 'Menu';

      const html = [];
      html.push('<ul class="header__mobile-list" role="list">');

      if (currentNode.url && currentNode.url !== '#') {
        html.push(
          `<li><a href="${this.escapeHtml(currentNode.url)}" class="header__mobile-all-link" data-mobile-link><span class="header__mobile-all-label">View all ${this.escapeHtml(currentNode.title || '')}</span><span class="header__mobile-link-arrow" aria-hidden="true">›</span></a></li>`
        );
      }

      links.forEach((link) => {
        const title = this.escapeHtml(link.title || 'Untitled');
        const normalizedUrl = typeof link.url === 'string' ? link.url.trim() : '';
        const hasValidUrl = normalizedUrl && normalizedUrl !== '#';
        const url = this.escapeHtml(hasValidUrl ? normalizedUrl : '#');
        const children = Array.isArray(link.links) ? link.links : [];

        if (children.length > 0) {
          const payload = this.escapeHtml(
            JSON.stringify({
              title: link.title || 'Menu',
              url: link.url || '',
              links: children
            })
          );

          html.push(
            `<li><button type="button" class="header__mobile-next" data-mobile-next="${payload}"><span class="header__mobile-next-label">${title}</span><span class="header__mobile-next-arrow" aria-hidden="true">›</span></button></li>`
          );
        } else {
          if (hasValidUrl) {
            html.push(
              `<li><a href="${url}" class="header__mobile-link" data-mobile-link><span class="header__mobile-link-label">${title}</span><span class="header__mobile-link-arrow" aria-hidden="true">›</span></a></li>`
            );
          } else {
            html.push(
              `<li><span class="header__mobile-link header__mobile-link--disabled"><span class="header__mobile-link-label">${title}</span><span class="header__mobile-link-arrow" aria-hidden="true">›</span></span></li>`
            );
          }
        }
      });

      if (!links.length && (!currentNode.url || currentNode.url === '#')) {
        html.push('<li class="header__mobile-list-empty">No links are available in this menu yet.</li>');
      }

      html.push('</ul>');
      this.mobileBranchList.innerHTML = html.join('');

      if (this.canAnimate()) {
        const sequence = [
          [this.mobileBranchList, { opacity: [0, 1], x: [14, 0] }, { duration: 0.3, easing: EASING_ELEGANT }],
        ];
        const branchItems = Array.from(this.mobileBranchList.querySelectorAll('li'));
        branchItems.forEach((item, i) => {
          const delay = Math.min(0.12, i * 0.02);
          sequence.push([item, { opacity: [0, 1], y: [8, 0] }, { duration: 0.28, easing: EASING_ELEGANT, at: delay + 0.04 }]);
        });
        window.Motion.animate(sequence);
      }
    }

    bindSearchExperience() {
      if (!this.searchOpenButton || !this.searchPanel || !this.searchOverlay) return;
      this.ensureSearchClosedState();

      this.searchOpenButton.addEventListener('click', () => {
        this.openDesktopSearch(this.searchOpenButton);
      }, { signal: this.signal });

      this.searchCloseButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.closeDesktopSearch();
        }, { signal: this.signal });
      });

      this.searchOverlay.addEventListener('click', () => {
        this.closeDesktopSearch();
      }, { signal: this.signal });

      this.searchChipButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const query = button.dataset.searchChipQuery;
          if (!query) return;

          const context = this.resolveSearchContext(button);
          this.applySearchQuery(query, context, { focusInput: true });
        }, { signal: this.signal });
      });

      this.root.addEventListener('click', (event) => {
        const refineButton = event.target.closest('[data-search-refine-query]');
        if (!refineButton) return;

        const query = refineButton.dataset.searchRefineQuery;
        if (!query) return;

        const context = this.resolveSearchContext(refineButton);
        this.applySearchQuery(query, context, { focusInput: true });
      }, { signal: this.signal });

      this.desktopSearchInput?.addEventListener('input', () => {
        const query = (this.desktopSearchInput.value || '').trim();
        this.updateSearchFromQuery(query, 'desktop');
      }, { signal: this.signal });

      this.mobileSearchInput?.addEventListener('focus', () => {
        const query = (this.mobileSearchInput.value || '').trim();
        if (!query) this.showGuidedSearch('mobile');
        const form = this.mobileSearchInput.closest('.header__mobile-search-form');
        if (form) form.classList.add('is-focused');
        if (this.mobileDrawer) this.mobileDrawer.classList.add('is-searching');
      }, { signal: this.signal });

      const mobileSearchClose = this.root.querySelector('[data-header-mobile-search-close]');
      mobileSearchClose?.addEventListener('click', async () => {
        const form = this.mobileSearchInput.closest('.header__mobile-search-form');
        const { guided, predictive } = this.getSearchNodes('mobile');
        const visibleContent = predictive && !predictive.hidden ? predictive : (guided && !guided.hidden ? guided : null);

        if (visibleContent && this.canAnimate()) {
          const sequence = [
            [visibleContent, { opacity: [1, 0], y: [0, 6] }, { duration: 0.18, easing: EASING_ELEGANT }],
          ];
          const controls = window.Motion.animate(sequence);
          if (controls?.finished) {
            try { await controls.finished; } catch (e) { /* cancelled */ }
          } else {
            await this.wait(180);
          }
          this.resetInlineStyles(visibleContent);
        }

        this.mobileSearchInput.value = '';
        this.mobileSearchInput.blur();
        if (form) form.classList.remove('is-focused');
        if (this.mobileDrawer) this.mobileDrawer.classList.remove('is-searching');
        this.hideGuidedSearch('mobile');
        this.hidePredictiveSearch('mobile');
      }, { signal: this.signal });

      this.mobileSearchInput?.addEventListener('blur', (e) => {
        const form = this.mobileSearchInput.closest('.header__mobile-search-form');
        const searchContainer = this.mobileSearchInput.closest('.header__mobile-search');
        if (form && !searchContainer?.contains(e.relatedTarget)) {
          setTimeout(() => {
            if (document.activeElement !== this.mobileSearchInput && !searchContainer?.contains(document.activeElement)) {
              form.classList.remove('is-focused');
              if (this.mobileDrawer) this.mobileDrawer.classList.remove('is-searching');
            }
          }, 150);
        }
      }, { signal: this.signal });

      this.mobileSearchInput?.addEventListener('input', () => {
        const query = (this.mobileSearchInput.value || '').trim();
        this.updateSearchFromQuery(query, 'mobile');
      }, { signal: this.signal });

      this.desktopSearchInput?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing) return;
        event.preventDefault();
        event.stopPropagation();
        const query = (this.desktopSearchInput.value || '').trim();
        if (query) this.updateSearchFromQuery(query, 'desktop');
      }, { signal: this.signal });

      this.mobileSearchInput?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing) return;
        event.preventDefault();
        event.stopPropagation();
        const query = (this.mobileSearchInput.value || '').trim();
        if (query) this.updateSearchFromQuery(query, 'mobile');
      }, { signal: this.signal });
    }

    ensureSearchClosedState() {
      this.desktopSearchOpen = false;
      this.searchCloseInProgress = false;
      delete this.root.dataset.searchOpen;
      this.searchInline?.classList.remove('is-open');

      if (this.desktopSearchForm) this.desktopSearchForm.hidden = true;
      this.desktopSearchForm?.classList.remove('is-closing');
      if (this.searchOpenButton) {
        this.searchOpenButton.hidden = false;
        this.searchOpenButton.setAttribute('aria-expanded', 'false');
      }

      if (this.searchOverlay) {
        this.searchOverlay.hidden = true;
        this.searchOverlay.setAttribute('aria-hidden', 'true');
        this.resetInlineStyles(this.searchOverlay);
      }

      if (this.searchPanel) {
        this.clearDesktopSearchRevealStyles();
        this.searchPanel.hidden = true;
        this.searchPanel.setAttribute('aria-hidden', 'true');
        this.resetInlineStyles(this.searchPanel);
      }

      if (this.desktopSearchForm) {
        this.resetInlineStyles(this.desktopSearchForm);
      }
    }

    nextSearchAnimationToken() {
      this.searchAnimationToken += 1;
      return this.searchAnimationToken;
    }

    isCurrentSearchAnimation(token) {
      return token === this.searchAnimationToken;
    }

    getSearchInput(context = 'desktop') {
      return context === 'mobile' ? this.mobileSearchInput : this.desktopSearchInput;
    }

    resolveSearchContext(element) {
      if (element?.closest('.header__mobile-search')) return 'mobile';
      return 'desktop';
    }

    applySearchQuery(rawQuery, context = 'desktop', { focusInput = false } = {}) {
      const query = String(rawQuery || '').trim();
      if (!query) return;

      const input = this.getSearchInput(context);
      if (!input) return;

      input.value = query;
      this.updateSearchFromQuery(query, context);

      if (focusInput) input.focus();
    }

    getDesktopSearchRevealTargets() {
      if (!this.searchPanel) return [];

      return Array.from(
        this.searchPanel.querySelectorAll(
          '.header__search-left-group, .header__search-card-item, .header__search-right-empty'
        )
      ).filter((element) => !element.hidden);
    }

    prepareDesktopSearchRevealStyles(elements) {
      if (!elements?.length) return;

      elements.forEach((element) => {
        element.style.opacity = '0';
        element.style.transform = 'translate3d(0, 8px, 0)';
      });
    }

    clearDesktopSearchRevealStyles(elements = this.getDesktopSearchRevealTargets()) {
      if (!elements?.length) return;

      elements.forEach((element) => this.resetInlineStyles(element));
    }

    async openDesktopSearch(triggerEl) {
      if (this.desktopSearchOpen || this.searchCloseInProgress) return;
      if (!this.searchPanel || !this.searchOverlay) return;
      const animationToken = this.nextSearchAnimationToken();

      this.closeDesktopItems();
      await this.closeMobileDrawer({ restoreFocus: false });
      if (!this.isCurrentSearchAnimation(animationToken)) return;

      this.lastFocusedTrigger = triggerEl || null;
      this.desktopSearchOpen = true;
      this.root.dataset.searchOpen = 'true';
      if (this.desktopSearchForm) {
        this.desktopSearchForm.classList.remove('is-closing');
        this.desktopSearchForm.hidden = false;
      }
      this.searchOpenButton?.setAttribute('aria-expanded', 'true');
      if (this.searchOpenButton) this.searchOpenButton.hidden = true;
      this.searchInline?.classList.add('is-open');
      this.searchOverlay.hidden = false;
      this.searchOverlay.setAttribute('aria-hidden', 'false');
      this.searchPanel.hidden = false;
      this.searchPanel.setAttribute('aria-hidden', 'false');
      this.syncSectionLayer();

      this.showGuidedSearch('desktop');
      const revealTargets = this.getDesktopSearchRevealTargets();

      if (this.canAnimate()) {
        this.prepareDesktopSearchRevealStyles(revealTargets);

        const sequence = [
          [this.searchOverlay, { opacity: [0, 1] }, { duration: 0.28, easing: EASING_ELEGANT }],
          [this.desktopSearchForm, { opacity: [0.8, 1] }, { duration: 0.3, easing: EASING_ELEGANT, at: '<' }],
          [this.searchPanel, { opacity: [0, 1], y: [-10, 0] }, { duration: 0.34, easing: EASING_ELEGANT, at: '<' }],
        ];

        // Stagger reveal targets after a slight delay
        revealTargets.forEach((el, i) => {
          const delay = Math.min(0.14, i * 0.02);
          sequence.push(
            [el, { opacity: [0, 1], y: [10, 0] }, { duration: 0.28, easing: EASING_ELEGANT, at: delay + 0.06 }]
          );
        });

        const controls = window.Motion.animate(sequence);
        if (controls?.finished) {
          try { await controls.finished; } catch (e) { /* cancelled */ }
        } else {
          await this.wait(400);
        }
        if (!this.isCurrentSearchAnimation(animationToken) || !this.desktopSearchOpen) return;
      }

      if (!this.isCurrentSearchAnimation(animationToken) || !this.desktopSearchOpen) return;

      window.setTimeout(() => {
        if (!this.desktopSearchOpen || !this.isCurrentSearchAnimation(animationToken)) return;
        this.desktopSearchInput?.focus();
      }, 0);
    }

    async closeDesktopSearch({ restoreFocus = true, clearQuery = true } = {}) {
      if (!this.desktopSearchOpen) return;
      if (this.searchCloseInProgress) return;
      this.searchCloseInProgress = true;
      const animationToken = this.nextSearchAnimationToken();

      try {
        this.searchInline?.classList.remove('is-open');
        this.desktopSearchForm?.classList.add('is-closing');

        if (this.canAnimate()) {
          const sequence = [
            [this.searchPanel, { opacity: [1, 0], y: [0, -8] }, { duration: 0.24, easing: EASING_ELEGANT }],
            [this.searchOverlay, { opacity: [1, 0] }, { duration: 0.22, easing: EASING_ELEGANT, at: '<' }],
          ];
          const controls = window.Motion.animate(sequence);
          if (controls?.finished) {
            try { await controls.finished; } catch (e) { /* cancelled */ }
          } else {
            await this.wait(240);
          }
        }
        if (!this.isCurrentSearchAnimation(animationToken)) return;

        this.desktopSearchOpen = false;
        delete this.root.dataset.searchOpen;
        this.searchOpenButton?.setAttribute('aria-expanded', 'false');

        if (this.searchOverlay) {
          this.searchOverlay.hidden = true;
          this.searchOverlay.setAttribute('aria-hidden', 'true');
          this.resetInlineStyles(this.searchOverlay);
        }

        if (this.searchPanel) {
          this.clearDesktopSearchRevealStyles();
          this.searchPanel.hidden = true;
          this.searchPanel.setAttribute('aria-hidden', 'true');
          this.resetInlineStyles(this.searchPanel);
        }

        await this.waitForTransition(this.searchInline, 'width', 460);
        if (!this.isCurrentSearchAnimation(animationToken)) return;

        if (this.desktopSearchForm) {
          this.desktopSearchForm.hidden = true;
          this.desktopSearchForm.classList.remove('is-closing');
          this.resetInlineStyles(this.desktopSearchForm);
        }
        if (this.searchOpenButton) this.searchOpenButton.hidden = false;

        if (clearQuery && this.desktopSearchInput) {
          this.desktopSearchInput.value = '';
          this.showGuidedSearch('desktop');
        }

        this.clearSearchContext('desktop');
        this.syncSectionLayer();
        if (restoreFocus) this.restoreTriggerFocus();
      } finally {
        this.searchCloseInProgress = false;
      }
    }

    getSearchNodes(context) {
      if (context === 'mobile') {
        return {
          guided: this.mobileSearchGuided,
          predictive: this.mobileSearchPredictive,
          title: this.mobileSearchResultsTitle
        };
      }

      return {
        guided: this.desktopSearchGuided,
        predictive: this.desktopSearchPredictive,
        title: this.desktopSearchResultsTitle
      };
    }

    updateSearchFromQuery(query, context = 'desktop') {
      this.clearSearchContext(context);

      if (!query) {
        this.showGuidedSearch(context);
        return;
      }

      this.searchState[context].debounceTimer = window.setTimeout(() => {
        this.fetchPredictiveSearch(query, context);
      }, 200);
    }

    async fetchPredictiveSearch(query, context = 'desktop') {
      const predictiveUrl = this.root.dataset.predictiveUrl;
      const sectionId = this.root.dataset.predictiveSectionId;
      const { predictive } = this.getSearchNodes(context);

      if (!predictiveUrl || !sectionId || !predictive) {
        this.showPredictiveError(query, context);
        return;
      }

      if (this.searchState[context].requestController) {
        this.searchState[context].requestController.abort();
      }

      this.searchState[context].requestController = new AbortController();

      const params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', 'product,article');
      params.set('resources[limit]', '8');
      params.set('resources[limit_scope]', 'each');
      params.set('section_id', sectionId);

      try {
        const response = await fetch(`${predictiveUrl}?${params.toString()}`, {
          signal: this.searchState[context].requestController.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`Predictive search failed: ${response.status}`);
        }

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextContent = parsed.querySelector('[data-predictive-search]');

        if (!nextContent) {
          this.showPredictiveError(query, context);
          return;
        }

        if (predictive.innerHTML && this.canAnimate()) {
          const sequence = [
            [predictive, { opacity: [1, 0] }, { duration: 0.14, easing: EASING_ELEGANT }],
          ];
          const controls = window.Motion.animate(sequence);
          if (controls?.finished) {
            try { await controls.finished; } catch (e) { /* cancelled */ }
          } else {
            await this.wait(140);
          }
          this.resetInlineStyles(predictive);
        }

        predictive.innerHTML = nextContent.innerHTML;
        this.showPredictiveSearch(context);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.showPredictiveError(query, context);
      }
    }

    showGuidedSearch(context = 'desktop') {
      const { guided, predictive, title } = this.getSearchNodes(context);

      if (context === 'desktop') {
        if (title) title.textContent = 'Recommended';
      } else if (title) {
        title.hidden = true;
      }

      if (guided) {
        const hasGuidedContent = guided.children.length > 0;
        guided.hidden = context === 'mobile' ? !hasGuidedContent : false;

        if (!guided.hidden && context === 'mobile' && this.canAnimate()) {
          const chips = Array.from(guided.querySelectorAll('.header__mobile-search-chip'));
          if (chips.length) {
            const sequence = [];
            chips.forEach((chip, i) => {
              const delay = Math.min(0.1, i * 0.025);
              sequence.push([chip, { opacity: [0, 1], y: [6, 0], scale: [0.96, 1] }, { duration: 0.24, easing: EASING_ELEGANT, at: delay }]);
            });
            window.Motion.animate(sequence);
          }
        }
      }

      if (predictive) {
        predictive.hidden = true;
        predictive.innerHTML = '';
      }
    }

    hideGuidedSearch(context = 'desktop') {
      const { guided } = this.getSearchNodes(context);
      if (guided) guided.hidden = true;
    }

    hidePredictiveSearch(context = 'desktop') {
      const { predictive, title } = this.getSearchNodes(context);
      if (predictive) {
        predictive.hidden = true;
        predictive.innerHTML = '';
      }
      if (title) title.hidden = true;
    }

    showPredictiveSearch(context = 'desktop') {
      const { guided, predictive, title } = this.getSearchNodes(context);

      if (guided) guided.hidden = true;
      if (title) {
        title.textContent = 'Search Results';
        title.hidden = false;
      }

      if (!predictive) return;

      predictive.hidden = false;

      if (this.canAnimate()) {
        const sequence = [
          [predictive, { opacity: [0, 1], y: [10, 0] }, { duration: 0.28, easing: EASING_ELEGANT }],
        ];
        const listItems = Array.from(
          predictive.querySelectorAll(
            '.header-predictive-search__product, .header-predictive-search__article, .header-predictive-search__hint, .header-predictive-search__empty'
          )
        );
        listItems.forEach((item, i) => {
          const delay = Math.min(0.12, i * 0.02);
          sequence.push([item, { opacity: [0, 1], y: [8, 0] }, { duration: 0.24, easing: EASING_ELEGANT, at: delay + 0.04 }]);
        });
        window.Motion.animate(sequence);
      }
    }

    showPredictiveError(query, context = 'desktop') {
      const { predictive } = this.getSearchNodes(context);
      if (!predictive) return;

      const safeQuery = this.escapeHtml(query);

      predictive.innerHTML = `
        <p class="header-predictive-search__empty">We couldn't load quick results right now.</p>
        <p class="header-predictive-search__hint">Please keep typing or try another keyword for “${safeQuery}”.</p>
      `;

      this.showPredictiveSearch(context);
    }

    bindGlobalShortcuts() {
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          if (this.desktopSearchOpen) {
            this.closeDesktopSearch();
            return;
          }

          if (this.mobileDrawer && !this.mobileDrawer.hidden) {
            this.closeMobileDrawer();
            return;
          }

          this.closeDesktopItems();
        }
      }, { signal: this.signal });

      document.addEventListener('click', (event) => {
        if (!this.desktopSearchOpen) return;
        if (this.root.contains(event.target)) return;
        this.closeDesktopSearch({ restoreFocus: false });
      }, { signal: this.signal });
    }

    activateFocusTrap(container) {
      if (!container) return;

      this.releaseFocusTrap();
      this.activeFocusTrap = { container };
      this.focusTrapHandler = (event) => {
        if (event.key !== 'Tab') return;

        const focusable = getFocusableElements(container);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      document.addEventListener('keydown', this.focusTrapHandler);
    }

    releaseFocusTrap() {
      if (!this.activeFocusTrap) return;
      if (this.focusTrapHandler) {
        document.removeEventListener('keydown', this.focusTrapHandler);
        this.focusTrapHandler = null;
      }
      this.activeFocusTrap = null;
    }

    restoreTriggerFocus() {
      if (this.lastFocusedTrigger && typeof this.lastFocusedTrigger.focus === 'function') {
        this.lastFocusedTrigger.focus();
      }

      this.lastFocusedTrigger = null;
    }

    clearSearchContext(context) {
      const state = this.searchState[context];
      if (!state) return;

      if (state.debounceTimer) {
        window.clearTimeout(state.debounceTimer);
        state.debounceTimer = null;
      }

      if (state.requestController) {
        state.requestController.abort();
        state.requestController = null;
      }
    }

    clearAllSearchState() {
      this.clearSearchContext('desktop');
      this.clearSearchContext('mobile');
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    }
  }

  const initHeaders = (scope = document) => {
    const roots = Array.from(scope.querySelectorAll('[data-header-nav]'));

    roots.forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new HeaderNavController(root));
    });
  };

  const destroyHeader = (root) => {
    const controller = controllers.get(root);
    if (!controller) return;
    controller.destroy();
    controllers.delete(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHeaders(), { once: true });
  } else {
    initHeaders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.matches?.('.section-header')) {
      initHeaders(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.('[data-header-nav]');
    if (root) destroyHeader(root);
  });
})();
